/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { ReactNode, useEffect, useState } from 'react';
import { RouteObject, useNavigate } from 'react-router';
import { Routes, Route, Navigate } from 'react-router-dom';

// Hooks
import { usePageTitle } from '../../../hooks/usePageTitle';

// Theme MUI
import { CssBaseline, Box } from '@mui/material';

// Routes
import { CustomRouteObject, routes } from '../../../routes';

// Services
import AuthService from '../../../services/auth.service';
import Authenticate from '../../pages/authenticate/login';

// Components
import HeaderBar from './HeaderBar';
import SideBar from './SideBar';
import Footer from './Footer';
import Breadcrumb from './Breadcrumb';

import { Spinner } from '../../common/Spinner';
import TimeModeToggle from '../../common/TimeModeToggle';

// Hooks
import useTimeMode from '../../../hooks/useTimeMode';
import { AppPermissions } from '../../../constants/Permissions';

import './Layout.scss';

const localStorageKey_permissions = 'permissions';
const externalAllowedRoutePaths = new Set(['dashboard', 'stockDetails', 'visits', 'visits/:id', 'search/:wildcard']);

const canExternalUserAccessRoute = (route: CustomRouteObject): boolean => {
  return !!route.path && externalAllowedRoutePaths.has(route.path);
};

export const withRouter = (Component: any) => {
  const Wrapper = (props: any) => {
    const history = useNavigate();

    return <Component history={history} {...props} />;
  };

  return Wrapper;
};

const Layout = () => {
  usePageTitle();

  const { timeMode, setTimeMode } = useTimeMode();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);

  const [loadingPermission, setLoadingPermission] = useState(true);

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  useEffect(() => {
    AuthService.isAuthenticated().then(setIsAuthenticated);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(localStorageKey_permissions);

      if (stored) {
        const perms = JSON.parse(stored) as string[];

        const hasWebAppUse = perms.includes(AppPermissions.WebApp.Use);

        setHasPermission(hasWebAppUse);
      } else {
        setHasPermission(false);
      }
    } catch (err) {
      console.error('Failed to load permission', err);
      setHasPermission(false);
    } finally {
      setLoadingPermission(false);
    }
  }, []);

  const renderRoute = (value: RouteObject, index: number, element?: ReactNode) => {
    return (
      <Route key={index} path={value.path} element={element || value.element}>
        {value.children?.map((route, i) => renderRoute(route, i))}
      </Route>
    );
  };

  const renderCustomRoute = (value: CustomRouteObject, index: number) => {
    if (value.isAuthGuarded && isAuthenticated === undefined) {
      return null;
    }

    const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

    if (isAuthenticated && isExternalUser && !canExternalUserAccessRoute(value)) {
      return renderRoute(value, index, <Navigate to='/dashboard' replace />);
    }

    const element = !value.isAuthGuarded || isAuthenticated ? value.element : <Authenticate />;

    if (value.guardedPermission && isAuthenticated && !AuthService.hasPermission(value.guardedPermission)) {
      return null;
    }

    return renderRoute(value, index, element);
  };

  const defaultRoute = routes.find((x) => x.isDefault);

  const defaultPath = defaultRoute?.path ?? '/dashboard';
  if (!defaultRoute?.path) {
    return null;
  }

  if (isAuthenticated === undefined || loadingPermission || hasPermission === null) {
    return (
      <Spinner
        loading
        containerStyles={{
          width: '100%',
          height: '100vh',
        }}
      />
    );
  }

  if (isAuthenticated && hasPermission === false) {
    return (
      <>
        <HeaderBar hasPermission={hasPermission} />

        <Box className='layout__access-denied'>
          You are not allowed access to E-ULD Digital Portal. Please contact{' '}
          <a href='mailto:support@unilode.com' className='layout__access-denied-link'>
            support@unilode.com
          </a>{' '}
          for assistance.
        </Box>
      </>
    );
  }

  // Not authenticated → public shell (no header, sidebar, footer, or margin)
  if (!isAuthenticated) {
    return (
      <>
        <CssBaseline />
        <Routes>
          {routes.filter((r) => !r.isAuthGuarded).map((r, i) => renderRoute(r, i))}
          <Route path='*' element={<Authenticate />} />
        </Routes>
      </>
    );
  }

  // Authenticated but no WebApp.Use permission
  if (hasPermission === false) {
    return (
      <>
        <HeaderBar hasPermission={hasPermission} />
        <Box className='layout__access-denied'>
          You are not allowed access to E-ULD Digital Portal. Please contact{' '}
          <a href='mailto:support@unilode.com' className='layout__access-denied-link'>
            support@unilode.com
          </a>{' '}
          for assistance.
        </Box>
      </>
    );
  }

  // Authenticated + permitted → full app shell
  return (
    <>
      <CssBaseline />
      <div className='app'>
        <SideBar />
        <main className='content'>
          <HeaderBar hasPermission={hasPermission} />
          <div className='breadcrumb-bar'>
            <Breadcrumb />

            <TimeModeToggle value={timeMode} onChange={setTimeMode} className='breadcrumb-bar__time-mode' />
          </div>
          <div className='content_body'>
            <Box m='10px'>
              <Routes>
                <Route path='' element={<Navigate to={defaultPath} replace />} />
                {routes.filter((r) => r.isAuthGuarded).map(renderCustomRoute)}
                <Route path='*' element={<Navigate to={defaultPath} replace />} />
              </Routes>
            </Box>
            <Footer />
          </div>
        </main>
      </div>
    </>
  );
};

export default Layout;
